import React, { useState, useEffect } from "react"
import { Redirect, useHistory } from "react-router-dom"
import { view } from "@risingstack/react-easy-state"
import styled from "styled-components"
import { EyeOutlined } from "@ant-design/icons"
import { Table, Tag, Button, Switch, Input, DatePicker, Select, message } from 'antd'

import { staleWhileRevalidate } from "utils/render-strategies"
import { hideExportButtons } from "utils/export"
import { getDefaultDateRange } from "utils/dateRange"
import UsersService from "services/users/users.service"
import globalStore from "store"

import StyledBox from "components/common/styled-components/StyledBox"

const StyledPageContent = styled.div`
  & > .action-buttons {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 30px;
  }
`

const UsersIndexPage = () => {
  const pageState = globalStore.ui.users.index
  const history = useHistory()
  const [tableLoading, setTableLoading] = useState(false)
  const [dateRange, setDateRange] = useState(getDefaultDateRange)
  const [statusFilter, setStatusFilter] = useState('all')
  const [exportingUsers, setExportingUsers] = useState(false)
  const accessibleFeatures = globalStore.currentUser?.accessible_features || []

  useEffect(() => {
    const fetchData = async () => {
      const pageState = globalStore.ui.users.index
      setTableLoading(true)
      await UsersService.index({ pageState })
      setTableLoading(false)
    }

    fetchData()
	}, [])

	const onStatusChange = async (userId) => {
		const tempState = {}
		await UsersService.updateStatus({ pageState: tempState, userId })
	}
	const onSearch = async (email) => {
			setTableLoading(true)
			await UsersService.index({ pageState,email })
			setTableLoading(false)
	}

	const handleDateChange = (dates) => {
		setDateRange(dates)
	}

	const handleStatusFilterChange = (value) => {
		setStatusFilter(value)
	}

	const handleExportUsers = async () => {
		const tempState = {}
		setExportingUsers(true)
		const params = {
			start_date: dateRange ? dateRange[0].format('YYYY-MM-DD') : undefined,
			end_date: dateRange ? dateRange[1].format('YYYY-MM-DD') : undefined,
			status: statusFilter,
		}
		try {
			await UsersService.userMappingReport({ pageState: tempState, params })

			const { serverStatus } = tempState

			if (serverStatus?.status === 200) {
				message.destroy()
				message.info('Export started')
				return
			}
			message.error('Failed to export user mapping report')
		} catch (error) {
			message.error('Failed to export user mapping report')
		} finally {
			setExportingUsers(false)
		}
	}

  const getColumns = (roles) => {
    let columns = [
			{
				title: '#',
				dataIndex: 'sn',
				key: 'sn',
				width: 50,
				fixed: 'left',
			},
			{
				title: 'Name',
				dataIndex: 'name',
				key: 'name',
				width: 220,
				fixed: 'left',
			},
			{
				title: 'Email',
				dataIndex: 'email',
				key: 'email',
				width: 220,
				fixed: 'left',
			},
			{
				title: 'Roles',
				dataIndex: 'roles',
				key: 'roles',
				fixed: 'left',
				render: (roles) => {
					return roles.map((r, i) => <Tag key={i}>{r}</Tag>)
				},
			},
			{
				title: 'Status',
				dataIndex: 'status',
				key: 'status',
				fixed: 'left',
				render: (status, row) => (
					<Switch
						size='small'
						defaultChecked={status === 'active' ? true : false}
						onChange={() => onStatusChange(row.key)}
					/>
				),
			},
			{
				title: 'Action',
				dataIndex: 'action',
				key: 'action',
				width: 50,
				render: (value, row) => (
					<Button type='link' onClick={() => history.push(`/users/${row.key}`)}>
						<EyeOutlined />
					</Button>
				),
			},
		]

    return columns
  }

  const users = pageState.users || []

  const columns = getColumns()

  const tableData = users.map((u, i) => {
    const retval = {
			key: u.id,
			sn: i + 1,
			name: u.name,
			email: u.email,
			roles: u.organization_roles.map((r) => r.name),
			status:u.status
		}

    return retval
  })

  const renderPageContent = () => {
    return (
			<StyledPageContent>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginBottom: '20px',
					}}>
					<Input.Search
						placeholder='Email'
						allowClear
						onSearch={onSearch}
						style={{ width: 200 }}
					/>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 10,
						}}>
						<strong style={{ padding: '0 5px' }}>Period :</strong>
						<DatePicker.RangePicker
							format='DD-MMM-YYYY'
							value={dateRange}
							onChange={handleDateChange}
						/>
						<Select
							style={{ width: 140 }}
							value={statusFilter}
							onChange={handleStatusFilterChange}>
							<Select.Option value='all'>All</Select.Option>
							<Select.Option value='active'>Active</Select.Option>
							<Select.Option value='inactive'>Inactive</Select.Option>
						</Select>
						{hideExportButtons(accessibleFeatures) && (
							<Button
								loading={exportingUsers}
								disabled={!dateRange || !dateRange[0] || !dateRange[1]}
								onClick={handleExportUsers}>
								{exportingUsers ? 'Exporting...' : 'Export'}
							</Button>
						)}
						<Button type='primary' ghost onClick={() => history.push('/users/new')}>
							Create User
						</Button>
					</div>
				</div>
				<Table loading={tableLoading} dataSource={tableData} columns={columns} />
			</StyledPageContent>
		)
  }

  if (
    accessibleFeatures?.length > 0 &&
    !accessibleFeatures.includes("user-view")
  )
    return <Redirect to="/" />

  const { API_USERS_INDEX_STATUS } = pageState

  return (
    <div className="page-content users-page">
      <StyledBox>
        {staleWhileRevalidate({
          status: API_USERS_INDEX_STATUS,
          data: users,
          render: renderPageContent,
        })}
      </StyledBox>
    </div>
  )
}

export default view(UsersIndexPage)
